import type { FinanceTransaction } from '../types/finance';
import type { FinanceRecord } from '../types/newFinance';
import { OFFERING_TYPE_LABELS, EXPENSE_CATEGORY_LABELS } from '../types/newFinance';

// 구 버전 CSV 헤더 생성
const generateHeaders = () => {
  return ['날짜', '구분', '항목', '내용/헌금자', '금액', '통화', '처리자'];
};

// 구 버전 CSV 데이터 행 생성
const generateCsvRow = (transaction: FinanceTransaction) => {
  return [
    transaction.date,
    transaction.type === 'income' ? '수입' : '지출',
    transaction.category,
    transaction.memberName || transaction.description || '',
    transaction.amount.toString(),
    transaction.currency,
    transaction.handler || ''
  ];
};

// 구 버전 CSV 문자열 생성
export const exportTransactionsToCsv = (transactions: FinanceTransaction[], filename: string = 'transactions') => {
  const headers = generateHeaders();
  const rows = transactions.map(generateCsvRow);

  // BOM 추가하여 한글이 깨지지 않도록 함
  const BOM = '\uFEFF';
  const csvContent = [
    BOM,
    headers.join(','),
    ...rows.map(row => row.map(field => `"${field}"`).join(',')) // 필드를 큰따옴표로 감싸서 쉼표 포함 처리
  ].join('\n');

  // Blob 생성 및 다운로드
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// 신 버전 재정 데이터 CSV 내보내기
export const exportFinanceRecordsToCsv = (records: FinanceRecord[], filename: string = '연간재정내역') => {
  const BOM = '\uFEFF';
  const headers = ['날짜', '구분', '카테고리', '이름/설명', '금액', '통화', '작성자', '상태'];

  const rows: string[][] = [];

  records.forEach(record => {
    // 십일조
    record.titheItems.forEach(item => {
      rows.push([
        record.date,
        '수입',
        '십일조',
        item.name,
        item.amount.toString(),
        item.currency,
        record.createdBy,
        record.status === 'approved' ? '승인완료' : record.status === 'pending' ? '승인대기' : '재승인요청'
      ]);
    });

    // 감사헌금
    record.thanksgivingItems.forEach(item => {
      rows.push([
        record.date,
        '수입',
        '감사헌금',
        item.name,
        item.amount.toString(),
        item.currency,
        record.createdBy,
        record.status === 'approved' ? '승인완료' : record.status === 'pending' ? '승인대기' : '재승인요청'
      ]);
    });

    // 기타 헌금
    record.otherIncomeItems.forEach(item => {
      const categoryLabel = OFFERING_TYPE_LABELS[item.category as keyof typeof OFFERING_TYPE_LABELS] || item.category;
      rows.push([
        record.date,
        '수입',
        categoryLabel,
        item.name || item.description || '',
        item.amount.toString(),
        item.currency,
        record.createdBy,
        record.status === 'approved' ? '승인완료' : record.status === 'pending' ? '승인대기' : '재승인요청'
      ]);
    });

    // 지출
    record.expenseItems.forEach(item => {
      const categoryLabel = EXPENSE_CATEGORY_LABELS[item.category];
      rows.push([
        record.date,
        '지출',
        categoryLabel,
        item.description,
        item.amount.toString(),
        item.currency,
        record.createdBy,
        record.status === 'approved' ? '승인완료' : record.status === 'pending' ? '승인대기' : '재승인요청'
      ]);
    });
  });

  const csvContent = [
    BOM,
    headers.join(','),
    ...rows.map(row => row.map(field => `"${field}"`).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
